import React from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cls, can } from "../ui";
import { EDITIONS, viewParam } from "./editions";

/** Every role edition renders inside this frame: breadcrumb header, docked actions, one view strip, ONE panel at a time. */
export function useEditionView(editionKey, access, extra = {}) {
  const [sp, setSp] = useSearchParams();
  const ed = EDITIONS.find((e) => e.key === editionKey);
  const views = (ed?.views || []).filter((v) => (!v.perm || can(access, ...v.perm)) && (!v.cond || (typeof v.cond === "function" ? v.cond(access, extra) : extra[v.cond])));
  const requested = viewParam(sp);
  const view = views.some((v) => v.key === requested) ? requested : views[0]?.key;
  const setView = (k) => setSp({ view: k });
  return { ed, views, view, setView, sp };
}

export function EditionFrame({ edition, views, view, setView, title, subtitle, actions, kpis, children, testId, counts = {} }) {
  const Icon = edition?.icon;
  const current = views.find((v) => v.key === view);
  return (
    <div className="flex flex-col flex-1 min-h-0 gap-1.5" data-testid={testId || `${edition?.key}-edition`}>
      <div className="ent-edition-hdr" data-testid="edition-header">
        <div className="flex items-center gap-1.5 min-w-0">
          {Icon && <Icon className="w-4 h-4 text-ujms-dark shrink-0" />}
          <h1 className="ent-edition-title truncate">{title || edition?.label}</h1>
          {current && <><ChevronRight className="w-3 h-3 text-slate-400 shrink-0" /><span className="text-[12px] font-semibold text-slate-700 truncate" data-testid="edition-current-view">{current.label}</span></>}
          {subtitle && <span className="text-[11px] text-slate-500 truncate hidden xl:inline">· {subtitle}</span>}
        </div>
        {actions && <div className="ml-auto flex items-center gap-1 shrink-0">{actions}</div>}
      </div>
      {kpis}
      {views.length > 1 && (
        <div className="ent-viewstrip" role="tablist" data-testid="edition-views">
          {views.map((v) => { const VI = v.icon; return (
            <button key={v.key} role="tab" aria-selected={view === v.key} className={cls("ent-view", view === v.key && "ent-view-active")} onClick={() => setView(v.key)} data-testid={`${edition?.key}-tab-${v.key}`}>
              {VI && <VI className="w-3.5 h-3.5" />}{v.label}{counts[v.key] > 0 && <span className="ent-explorer-count">{counts[v.key]}</span>}
            </button>); })}
        </div>
      )}
      <div className="flex-1 min-h-0 flex flex-col gap-1.5" data-testid={`${edition?.key}-view-${view}`}>{children}</div>
    </div>
  );
}
